import { Session, StockSymbol } from '@benzinga/session';
import { Subscription, Subscribable, SubscriberId } from '@benzinga/subscribable';

import { Watchlist, WatchlistId, WatchlistManager } from '@benzinga/watchlist-manager';
import { QuotesV3FieldsManager } from '@benzinga/quotes-v3-fields-manager';

import { HoldingsQuote, QuoteHoldingsFeed, QuotesV3HoldingsManager } from '@benzinga/quotes-v3-holdings-manager';
import { FieldTracker, Quote } from '@benzinga/quotes-v3-manager';

interface QuoteUpdateEvent {
  quote: HoldingsQuote;
  type: 'watchlist-holdings:quote-update';
}

interface QuotesUpdateEvent {
  quotes: HoldingsQuote[];
  type: 'watchlist-holdings:update';
}

export type WatchlistHoldingsFeedEvent = QuoteUpdateEvent | QuotesUpdateEvent;
export class WatchlistHoldingsFeed extends Subscribable<WatchlistHoldingsFeedEvent> {
  protected subscriptions: Map<SubscriberId, Set<keyof Quote>> = new Map();

  private session: Session;
  private watchlist: Watchlist | undefined;
  private watchlistId: WatchlistId;
  private watchlistManager: WatchlistManager;
  private watchlistSubscription: Subscription<WatchlistManager> | undefined;
  private symbols: StockSymbol[] = [];
  private quotesFeed: QuoteHoldingsFeed;
  private quotesFeedSubscription: Subscription<QuoteHoldingsFeed> | undefined;

  private setIntervalHandle: ReturnType<typeof setInterval> | undefined;

  private subscribeToQuotes: boolean;
  private fieldsTracker: FieldTracker;

  constructor(watchlistId: WatchlistId, subscribeToQuotes: boolean, session: Session, visibleSymbols?: StockSymbol[]) {
    super();
    this.watchlistId = watchlistId;
    this.subscribeToQuotes = subscribeToQuotes;

    this.session = session;
    this.fieldsTracker = new FieldTracker(
      this.onFieldsUpdate,
      session.getManager(QuotesV3FieldsManager).getFieldIds() as Set<keyof Quote>,
    );
    this.watchlistManager = session.getManager(WatchlistManager);
    this.watchlist = this.watchlistManager.getStoredWatchlistById(watchlistId);
    this.symbols = visibleSymbols ?? [];
    const quotesManager = session.getManager(QuotesV3HoldingsManager);
    this.quotesFeed = quotesManager.createQuotesFeed(this.symbols, subscribeToQuotes);
  }

  public getCachedValue = (): HoldingsQuote[] => this.quotesFeed.getCachedValue();

  public getSymbols = (): StockSymbol[] => this.getCachedValue().map(q => q.symbol);

  public getWatchlist = () => this.watchlist;
  public getWatchlistId = () => this.watchlistId;

  protected override onFirstSubscription(args: Set<keyof Quote>) {
    this.watchlistManager = this.session.getManager(WatchlistManager);
    this.watchlist = this.watchlistManager.getStoredWatchlistById(this.watchlistId);
    const quotesManager = this.session.getManager(QuotesV3HoldingsManager);
    this.quotesFeed = quotesManager.createQuotesFeed(this.symbols ?? [], this.subscribeToQuotes);

    this.quotesFeedSubscription = this.quotesFeed.subscribe(this.onSubscriptionFeedCall, args);

    this.updateValues();
    this.setIntervalHandle = setInterval(this.updateValues, 30000);

    this.watchlistSubscription = this.watchlistManager.subscribe(event => {
      switch (event.type) {
        case 'watchlist:updated_watchlists': {
          this.watchlist = this.watchlistManager.getStoredWatchlistById(this.watchlistId);
          const quotesManager = this.session.getManager(QuotesV3HoldingsManager);
          this.quotesFeed = quotesManager.createQuotesFeed(this.symbols ?? [], this.subscribeToQuotes);
          if (this.quotesFeedSubscription) {
            this.quotesFeedSubscription.unsubscribe();
            this.quotesFeedSubscription = this.quotesFeed.subscribe(
              this.onSubscriptionFeedCall,
              this.fieldsTracker.getFields(),
            );
          }
          this.dispatch({ quotes: this.quotesFeed.getCachedValue(), type: 'watchlist-holdings:update' });
        }
      }
    });

    this.dispatch({ quotes: this.getCachedValue(), type: 'watchlist-holdings:update' });
  }

  protected override onZeroSubscriptions() {
    if (this.setIntervalHandle) {
      clearInterval(this.setIntervalHandle);
    }
    this.quotesFeedSubscription?.unsubscribe();
    this.watchlistSubscription?.unsubscribe();

    this.quotesFeedSubscription = undefined;
    this.watchlistSubscription = undefined;
  }

  protected override onUpdate(id: SubscriberId, args: Set<keyof Quote>) {
    this.fieldsTracker.onUpdate(id, args);
  }

  protected override onSubscribe(id: SubscriberId, args: Set<keyof Quote>) {
    this.fieldsTracker.onSubscribe(id, args);
  }

  protected override onUnsubscribe(id: SubscriberId) {
    this.fieldsTracker.onUnsubscribe(id);
  }

  private onFieldsUpdate = (fields: Set<keyof Quote>) => {
    this.quotesFeedSubscription?.update(this.onSubscriptionFeedCall, fields);
  };

  private onSubscriptionFeedCall = event => {
    switch (event.type) {
      case 'quote-holdings:update':
        this.dispatch({ quote: event.quote, type: 'watchlist-holdings:quote-update' });
    }
  };

  private updateValues = () => {
    this.watchlistManager.getWatchlists(false);
  };
}
